import { useLightDismiss } from './lightDismiss';
import { useAvatarPeek } from './avatarPeek';
import { createContext, useContext, useEffect, useEffectEvent, useId, useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { gsap, useGSAP } from '../motion/gsap';
import { etaLabel, initials, shouldAnimate } from '../lib/format';
import { useToasts } from '../lib/session';
import { useT } from '../lib/i18n';
import type { Media } from '../lib/types';

// ---- Brand ------------------------------------------------------------------

/** App mark: a broadcast glyph (the same waves as the Connection card) in white on black. */
export function BrandMark({ size = 32 }: { size?: number }) {
  return (
    <div className="brand-mark" style={{ width: size, height: size }}>
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
        <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
        <circle cx="12" cy="12" r="2" fill="#fff" />
        <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
        <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19.1" />
      </svg>
    </div>
  );
}

// ---- Controls ---------------------------------------------------------------

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  label?: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={value === o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ id, on, onChange, label }: { id?: string; on: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" id={id} className={`toggle${on ? ' on' : ''}`} role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} />;
}

export function Avatar({
  name,
  dark,
  size,
  src,
  online,
  children,
}: {
  name: string;
  dark?: boolean;
  size?: 'lg' | 'xl';
  src?: string;
  online?: boolean;
  children?: ReactNode;
}) {
  const [broken, setBroken] = useState(false);
  const px = size === 'xl' ? 72 : undefined;
  // Press and hold a picture to see it large (ui/avatarPeek.tsx).
  const peek = useAvatarPeek(src && !broken ? src : undefined, name);
  return (
    <div
      className={`avatar${dark ? ' dark' : ''}${size === 'lg' ? ' lg' : ''}${src && !broken ? ' has-photo' : ''}`}
      style={px ? { width: px, height: px, fontSize: 24 } : undefined}
      aria-hidden
      {...peek.handlers}
    >
      {src && !broken ? <img src={src} alt="" draggable={false} onError={() => setBroken(true)} /> : initials(name)}
      {online !== undefined && <span className={`status-dot${online ? ' on' : ''}`} />}
      {children}
      {peek.overlay}
    </div>
  );
}

export function Dot({ tone }: { tone: 'green' | 'amber' | 'red' | 'gray' | 'accent' }) {
  return <span className={`dot dot-${tone}`} />;
}

// ---- Numbers ----------------------------------------------------------------

/** Animated number that tweens from its previous value. */
export function CountUp({ value, format = (n) => Math.round(n).toLocaleString() }: { value: number; format?: (n: number) => string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef(0);
  // The tween keeps calling the latest formatter without restarting when the caller passes a new one.
  const formatNow = useEffectEvent((n: number) => format(n));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!shouldAnimate()) {
      el.textContent = formatNow(value);
      prev.current = value;
      return;
    }
    const obj = { v: prev.current };
    const tween = gsap.to(obj, {
      v: value,
      duration: 0.9,
      ease: 'power2.out',
      onUpdate: () => {
        el.textContent = formatNow(obj.v);
      },
    });
    prev.current = value;
    return () => {
      tween.kill();
    };
  }, [value]);
  return <span ref={ref}>{format(prev.current)}</span>;
}

export function StatTile({ label, value, sub, dark }: { label: ReactNode; value: ReactNode; sub?: ReactNode; dark?: boolean }) {
  return (
    <div className={`stat${dark ? ' dark' : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub !== undefined && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// ---- Pipeline bar -----------------------------------------------------------

export interface PipelineSegment {
  key: string;
  label: string;
  count: number;
  color: string;
}

export function PipelineBar({ segments }: { segments: PipelineSegment[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const total = segments.reduce((a, s) => a + s.count, 0);
  useGSAP(
    () => {
      if (!shouldAnimate()) return;
      gsap.from('.pipeline-seg', { scaleX: 0, stagger: 0.07, duration: 0.8, ease: 'expo.out' });
    },
    { scope: ref, dependencies: [total] },
  );
  return (
    <div ref={ref} style={{ display: 'flex', gap: 6 }}>
      {segments.map((s) => (
        // An empty stage stays in place (the bar keeps its shape) but dims; each is at least as wide as its label.
        <div key={s.key} className={`pipeline-stage${s.count ? '' : ' idle'}`} style={{ flex: `${total ? Math.max(s.count, total * 0.15) : 1} 1 0`, minWidth: 'max-content', overflow: 'hidden' }}>
          <div className="pipeline-seg" style={{ background: s.color, height: 26 }} />
          <div className="pipeline-labels" style={{ display: 'block' }}>
            <b>{s.count}</b>
            <span>{s.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Radial bar gauge -------------------------------------------------------

export function Gauge({ value, bars = 46 }: { value: number; bars?: number }) {
  const ref = useRef<SVGSVGElement>(null);
  const pct = Math.max(0, Math.min(1, value));
  const filled = Math.round(pct * bars);
  const cx = 130,
    cy = 132,
    r1 = 74,
    r2 = 112;
  const start = 196,
    end = -16;
  useGSAP(
    () => {
      if (!shouldAnimate()) return;
      gsap.from('.g-on', { opacity: 0, scale: 0.4, transformOrigin: `${cx}px ${cy}px`, stagger: 0.012, duration: 0.5, ease: 'back.out(2)' });
    },
    { scope: ref, dependencies: [filled] },
  );
  return (
    <svg ref={ref} viewBox="0 0 260 150" width="100%" style={{ display: 'block' }} aria-hidden>
      {Array.from({ length: bars }, (_, i) => {
        const a = ((start + ((end - start) * i) / (bars - 1)) * Math.PI) / 180;
        const on = i < filled;
        const inner = on ? r1 : r1 + 10;
        return (
          <line
            key={i}
            className={on ? 'g-on' : 'g-off'}
            x1={cx + Math.cos(a) * inner}
            y1={cy - Math.sin(a) * inner}
            x2={cx + Math.cos(a) * r2}
            y2={cy - Math.sin(a) * r2}
            style={{ stroke: on ? '#121214' : 'color-mix(in srgb, var(--accent-ink) 28%, transparent)' }}
            strokeWidth={on ? 5 : 2}
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

// ---- Status chips -----------------------------------------------------------

export function MediaStatusChip({ m }: { m: Media }) {
  const t = useT();
  const pct = `${Math.round(m.progress * 100)}%`;
  switch (m.status) {
    case 'ready':
      return <span className="chip chip-accent">{t('pipe.ready')}</span>;
    case 'failed':
      return (
        <span className="chip chip-red" title={m.error}>
          {t('pipe.failed')}
        </span>
      );
    case 'uploading':
      return <span className="chip chip-gray">{`${t('pipe.uploading')} ${pct}`}</span>;
    case 'queued':
      return (
        <span className="chip chip-gray" title={m.error}>
          {m.attempts > 0 ? m.stage : t('pipe.queued')}
        </span>
      );
    default: {
      const left = etaLabel(m.eta);
      return (
        <span className="chip chip-amber" title={m.playable ? `Watchable now · ${m.stage}` : m.stage}>
          {`${m.playable ? '▶ ' : ''}${t('pipe.encoding')} ${pct}${left ? ` · ${left}` : ''}`}
        </span>
      );
    }
  }
}

// ---- Modal & toasts ---------------------------------------------------------

// The id a modal's <ModalTitle> takes, so the dialog is announced by its heading.
const ModalTitleId = createContext<string | undefined>(undefined);

/** A modal's heading (an <h2>); names the dialog for screen readers. */
export function ModalTitle(props: ComponentProps<'h2'>) {
  return <h2 {...props} id={useContext(ModalTitleId)} />;
}

export function Modal({ open, onClose, children, wide }: { open: boolean; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // A native modal <dialog>: the browser keeps keyboard focus inside it, closes it on Escape (the
  // cancel event) and hides the page behind it from screen readers.
  useEffect(() => {
    const d = ref.current;
    if (open && d && !d.open) d.showModal();
  }, [open]);
  useLightDismiss(ref, open);
  useGSAP(
    () => {
      if (!open || !shouldAnimate()) return;
      gsap.from('.modal', { y: 24, scale: 0.97, opacity: 0, duration: 0.45 });
      gsap.from(ref.current, { opacity: 0, duration: 0.25, ease: 'none' });
    },
    { scope: ref, dependencies: [open] },
  );
  if (!open) return null;
  return createPortal(
    <dialog
      ref={ref}
      className={`modal-dialog${wide ? ' wide' : ''}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault(); // React decides when it closes
        onClose();
      }}
      // Clicking outside closes it too (light dismiss); keyboard users use Escape or Close.
      closedby="any"
    >
      <div className={`modal${wide ? ' modal-wide' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-8px -8px 0 0' }}>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <ModalTitleId.Provider value={titleId}>{children}</ModalTitleId.Provider>
      </div>
    </dialog>,
    document.body,
  );
}

export function Toasts() {
  const toasts = useToasts((s) => s.toasts);
  const ref = useRef<HTMLDivElement>(null);
  // The stack lives on the top layer (a manual popover), raised again for each new toast, so it
  // shows above an open dialog instead of behind its backdrop.
  useEffect(() => {
    const el = ref.current as (HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void }) | null;
    if (!el?.showPopover || !toasts.length) return;
    try {
      el.hidePopover?.();
      el.showPopover();
    } catch {
      /* not supported: it stays a normal fixed layer */
    }
  }, [toasts.length]);
  useGSAP(
    () => {
      const last = ref.current?.lastElementChild;
      if (last && shouldAnimate()) gsap.from(last, { y: 18, scale: 0.92, opacity: 0, duration: 0.42, ease: 'back.out(1.7)' });
    },
    { scope: ref, dependencies: [toasts.length] },
  );
  return createPortal(
    <div className="toasts" ref={ref} aria-live="polite" popover="manual">
      {toasts.map((t) => (
        <div key={t.id} className="toast">
          <span className={`dot dot-${t.tone === 'red' ? 'red' : 'accent'}`} />
          {t.text}
        </div>
      ))}
    </div>,
    document.body,
  );
}
